import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './components/ThemeProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuthStore, useEntityStore } from './store';
import { entitiesAPI } from './lib/api';
import './index.css';

// Pages
import { LoginPage, AuthCallbackPage } from './pages/AuthPages';
import { OverallDashboardPage } from './pages/OverallDashboardPage';
import { DashboardPage } from './pages/DashboardPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { IntercompanyPage } from './pages/IntercompanyPage';
import { TaxReservesPage } from './pages/TaxReservesPage';
import { PayrollPage } from './pages/PayrollPage';
// ExpensesPage content merged into ProfitLossPage (Commit A)
import { WealthPage } from './pages/WealthPage';
import { AlertsPage } from './pages/AlertsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportingPage } from './pages/ReportingPage';
import { MonthReportPage } from './pages/MonthReportPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { SubscriptionsPage } from './pages/SubscriptionsPage';
import { FxRatesPage } from './pages/FxRatesPage';
import { SystemHealthPage } from './pages/SystemHealthPage';
import { ProfitLossPage } from './pages/ProfitLossPage';
// RevenuePage content merged into ProfitLossPage (Commit A)
import { ManagementStatementsPage } from './pages/ManagementStatementsPage';
import { RealEstatePortfolioPage } from './pages/RealEstatePortfolioPage';
import { PropertyPortfolioPage } from './pages/PropertyPortfolioPage';
import { BROSDashboardPage } from './pages/BROSDashboardPage';
// Old standalone pages replaced with redirects to canonical Finance tabs
import { BROSRevenuePage } from './pages/BROSRevenuePage';
import { BROSCostsPage } from './pages/BROSCostsPage';
import { BROSProfitLossPage } from './pages/BROSProfitLossPage';
import { BROSTaxReservesPage } from './pages/BROSTaxReservesPage';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { CostsPage } from './pages/CostsPage';
import { InvoicingPage } from './pages/InvoicingPage';
import { UplistingWebhookDiagnosticPage } from './pages/UplistingWebhookDiagnosticPage';
import { EntitiesAdminPage } from './pages/EntitiesAdminPage';
import { AttentionPage } from './pages/AttentionPage';
import { ControlPage } from './pages/ControlPage';
// CategorizationRulesPage moved to Banking sub-tab (Commit B)
// GroupCashForecastPage removed (Commit B)
import { PersonalNetWorthPage } from './pages/PersonalNetWorthPage';
import { PersonalIncomePage } from './pages/PersonalIncomePage';
import { PersonalProfitLossPage } from './pages/PersonalProfitLossPage';
import { EntitiesAssetsLiabilitiesPage } from './pages/EntitiesAssetsLiabilitiesPage';
import { GroupAssetsLiabilitiesPage } from './pages/GroupAssetsLiabilitiesPage';
import { GroupProfitLossPage } from './pages/GroupProfitLossPage';
import { CashflowPage } from './pages/CashflowPage';
import { OverallReportingPage } from './pages/OverallReportingPage';
import { OverallTaxPage, ScopedTaxPage } from './pages/OverallTaxPage';
import { PersonalSpendingsPage } from './pages/PersonalSpendingsPage';
import { PersonalSubscriptionsPage } from './pages/PersonalSubscriptionsPage';
import { BankingPage } from './pages/BankingPage';

// Protected Route Wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// App Initializer - fetches entities on auth
function AppInitializer({ children }) {
  const { isAuthenticated } = useAuthStore();
  const { setEntities, entities } = useEntityStore();

  const { selectedEntity, setSelectedEntity, setSelectedScope } = useEntityStore();

  useEffect(() => {
    const fetchEntities = async () => {
      if (isAuthenticated) {
        try {
          const { data } = await entitiesAPI.getAll();
          setEntities(data);

          // Migrate property_portfolio pseudo-entity to scope model
          const current = useEntityStore.getState().selectedEntity;
          if (current === 'property_portfolio') {
            setSelectedScope('portfolio');
            setSelectedEntity('all');
          } else if (current && current !== 'all') {
            // Migrate stale persisted selectedEntity (entity type -> entity ID)
            const isValidId = data.some((e) => e.id === current);
            if (!isValidId) {
              const match = data.find((e) => e.type === current);
              setSelectedEntity(match ? match.id : 'all');
            }
          }
        } catch (error) {
          console.error('Failed to fetch entities:', error);
        }
      }
    };
    fetchEntities();
  }, [isAuthenticated, setEntities, setSelectedEntity, setSelectedScope]);

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <AppInitializer>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            
            {/* Protected Routes */}
            <Route
              path="/profit-loss"
              element={
                <ProtectedRoute>
                  <ProfitLossPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/revenue"
              element={<Navigate to="/profit-loss?section=revenue" replace />}
            />
            <Route
              path="/real-estate"
              element={
                <ProtectedRoute>
                  <RealEstatePortfolioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overall"
              element={
                <ProtectedRoute>
                  <OverallDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/group"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/overall/assets-liabilities"
              element={
                <ProtectedRoute>
                  <GroupAssetsLiabilitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overall/profit-loss"
              element={
                <ProtectedRoute>
                  <GroupProfitLossPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overall/cashflow"
              element={
                <ProtectedRoute>
                  <CashflowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/taxes"
              element={
                <ProtectedRoute>
                  <ScopedTaxPage scope="entities" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/taxes"
              element={
                <ProtectedRoute>
                  <ScopedTaxPage scope="personal" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overall/tax"
              element={
                <ProtectedRoute>
                  <OverallTaxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/overall/reporting"
              element={
                <ProtectedRoute>
                  <OverallReportingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            {/* Old standalone routes redirect to Banking tabs */}
            <Route
              path="/transactions"
              element={<Navigate to="/banking/transactions" replace />}
            />
            <Route
              path="/reconciliation"
              element={<Navigate to="/banking/reconciliation" replace />}
            />
            <Route
              path="/review"
              element={<Navigate to="/invoices?section=review" replace />}
            />
            <Route
              path="/intercompany"
              element={
                <ProtectedRoute>
                  <IntercompanyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <ProtectedRoute>
                  <PayrollPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tax"
              element={
                <ProtectedRoute>
                  <TaxReservesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={<Navigate to="/profit-loss?section=costs" replace />}
            />
            <Route
              path="/wealth"
              element={
                <ProtectedRoute>
                  <WealthPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets-liabilities"
              element={
                <ProtectedRoute>
                  <EntitiesAssetsLiabilitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reporting"
              element={
                <ProtectedRoute>
                  <ReportingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reporting/:year/:month"
              element={
                <ProtectedRoute>
                  <MonthReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute>
                  <IntegrationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <SubscriptionsPage />
                </ProtectedRoute>
              }
            />
            {/* Cash Forecast retired (Commit B) */}
            <Route path="/forecast" element={<Navigate to="/dashboard" replace />} />
            <Route path="/cash-forecast" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/fx-rates"
              element={
                <ProtectedRoute>
                  <FxRatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/system-health"
              element={
                <ProtectedRoute>
                  <SystemHealthPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/management-statements"
              element={
                <ProtectedRoute>
                  <ManagementStatementsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/portfolio"
              element={
                <ProtectedRoute>
                  <PropertyPortfolioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal"
              element={<Navigate to="/personal/net-worth" replace />}
            />
            <Route
              path="/personal/net-worth"
              element={
                <ProtectedRoute>
                  <PersonalNetWorthPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/profit-loss"
              element={
                <ProtectedRoute>
                  <PersonalProfitLossPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/intercompany"
              element={
                <ProtectedRoute>
                  <IntercompanyPage scope="personal" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/intercompany/entries"
              element={
                <ProtectedRoute>
                  <IntercompanyPage scope="personal" />
                </ProtectedRoute>
              }
            />
            {/* Income retired: redirect to personal P&L revenue view */}
            <Route
              path="/personal/income"
              element={<Navigate to="/personal/profit-loss?section=revenue" replace />}
            />
            <Route
              path="/personal/spendings"
              element={
                <ProtectedRoute>
                  <PersonalSpendingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/subscriptions"
              element={
                <ProtectedRoute>
                  <PersonalSubscriptionsPage />
                </ProtectedRoute>
              }
            />
            {/* Old personal bank-balances redirects to Banking tabs */}
            <Route
              path="/personal/bank-balances"
              element={<Navigate to="/personal/banking/balances" replace />}
            />
            {/* Old bank-balances redirects to Banking tabs */}
            <Route
              path="/bank-balances"
              element={<Navigate to="/banking/balances" replace />}
            />

            {/* Banking section: entities scope */}
            <Route
              path="/banking/balances"
              element={
                <ProtectedRoute>
                  <BankingPage scope="entities" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banking/transactions"
              element={
                <ProtectedRoute>
                  <BankingPage scope="entities" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banking/reconciliation"
              element={
                <ProtectedRoute>
                  <BankingPage scope="entities" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banking/rules"
              element={
                <ProtectedRoute>
                  <BankingPage scope="entities" />
                </ProtectedRoute>
              }
            />
            {/* Banking section: personal scope */}
            <Route
              path="/personal/banking/balances"
              element={
                <ProtectedRoute>
                  <BankingPage scope="personal" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/banking/transactions"
              element={
                <ProtectedRoute>
                  <BankingPage scope="personal" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/personal/banking/reconciliation"
              element={
                <ProtectedRoute>
                  <BankingPage scope="personal" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/costs"
              element={
                <ProtectedRoute>
                  <CostsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoicing"
              element={
                <ProtectedRoute>
                  <InvoicingPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/bros-dashboard"
              element={
                <ProtectedRoute>
                  <BROSDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Old standalone routes redirect to canonical Finance tabs */}
            <Route
              path="/bros/client-payments"
              element={<Navigate to="/bros/revenue?tab=payments" replace />}
            />
            <Route
              path="/bros/supplier-payments"
              element={<Navigate to="/bros/costs?tab=bills-payments" replace />}
            />
            <Route
              path="/bros/reconciliation"
              element={<Navigate to="/bros/revenue?tab=reconciliation" replace />}
            />

            {/* BR OS Finance pages */}
            <Route
              path="/bros/revenue"
              element={
                <ProtectedRoute>
                  <BROSRevenuePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bros/costs"
              element={
                <ProtectedRoute>
                  <BROSCostsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bros/profit-loss"
              element={
                <ProtectedRoute>
                  <BROSProfitLossPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bros/tax-reserves"
              element={
                <ProtectedRoute>
                  <BROSTaxReservesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/diagnostics"
              element={
                <ProtectedRoute>
                  <DiagnosticsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/uplisting-diagnostic"
              element={
                <ProtectedRoute>
                  <UplistingWebhookDiagnosticPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/entities-admin"
              element={
                <ProtectedRoute>
                  <EntitiesAdminPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/control"
              element={
                <ProtectedRoute>
                  <ControlPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/attention"
              element={
                <ProtectedRoute>
                  <AttentionPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/group-forecast"
              element={<Navigate to="/dashboard" replace />}
            />

            <Route
              path="/categorization-rules"
              element={<Navigate to="/banking/rules" replace />}
            />

            {/* Default Redirect: Overall is the default landing */}
            <Route path="/" element={<Navigate to="/overall" replace />} />
            <Route path="*" element={<Navigate to="/overall" replace />} />
          </Routes>
        </AppInitializer>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            color: 'hsl(var(--foreground))',
          },
        }}
      />
    </ThemeProvider>
  );
}

export default App;
